import type {
  AuthorizationSubject,
  PortalCapabilityArea,
} from "@whitehaven/permissions";
import { ContentState } from "@whitehaven/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  adminSchedulingAuthorizationKey,
  canReadAdminScheduling,
  canWriteAdminScheduling,
} from "./scheduling-authorization";
import type {
  AdminSchedulingCatalogMutationGateway,
  AdminSchedulingCatalogReadGateway,
} from "./scheduling-gateway";
import {
  isAdminSchedulingCatalogSnapshot,
  type AdminSchedulingCatalogFocus,
  type AdminSchedulingCatalogMutationRequest,
  type AdminSchedulingCatalogSnapshot,
} from "./scheduling-portal-model";
import { createSchedulingIdempotencyKey } from "./scheduling-route-support";
import { AdminSchedulingCatalogView } from "./AdminSchedulingCatalogView";

export interface AdminSchedulingCatalogRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly capabilityArea: PortalCapabilityArea;
  readonly focus: AdminSchedulingCatalogFocus;
  readonly readGateway: AdminSchedulingCatalogReadGateway | null;
  readonly mutationGateway: AdminSchedulingCatalogMutationGateway | null;
}

type CatalogLoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly catalog: AdminSchedulingCatalogSnapshot;
    };

function CatalogFrame({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="portal-page" aria-labelledby="portal-title">
      <header className="portal-page__header">
        <p className="portal-kicker">Scheduling configuration</p>
        <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
          {title}
        </h1>
      </header>
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

export function AdminSchedulingCatalogRoute({
  subject,
  capabilityArea,
  focus,
  readGateway,
  mutationGateway,
}: AdminSchedulingCatalogRouteProps) {
  const authorized = canReadAdminScheduling(subject, capabilityArea);
  const subjectKey = subject ? adminSchedulingAuthorizationKey(subject) : null;
  const [loadState, setLoadState] = useState<CatalogLoadState>({
    kind: "loading",
  });
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const readSequence = useRef(0);
  const mutationSequence = useRef(0);
  const activeRead = useRef<AbortController | null>(null);
  const activeMutation = useRef<AbortController | null>(null);

  const load = useCallback(() => {
    if (!authorized || !readGateway || activeMutation.current) return;
    const sequence = readSequence.current + 1;
    readSequence.current = sequence;
    activeRead.current?.abort();
    const controller = new AbortController();
    activeRead.current = controller;
    const protectedStateGeneration = captureProtectedStateGeneration();
    setLoadState({ kind: "loading" });
    setFeedback(null);
    void readGateway({ signal: controller.signal }).then(
      (catalog) => {
        if (
          controller.signal.aborted ||
          readSequence.current !== sequence ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }
        setLoadState(
          isAdminSchedulingCatalogSnapshot(catalog)
            ? { kind: "ready", catalog }
            : { kind: "error" },
        );
      },
      () => {
        if (
          !controller.signal.aborted &&
          readSequence.current === sequence &&
          isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          setLoadState({ kind: "error" });
        }
      },
    );
  }, [authorized, readGateway]);

  useEffect(() => {
    const clearState = (visible: boolean) => {
      readSequence.current += 1;
      mutationSequence.current += 1;
      activeRead.current?.abort();
      activeMutation.current?.abort();
      activeRead.current = null;
      activeMutation.current = null;
      if (visible) {
        setLoadState({ kind: "loading" });
        setBusy(false);
        setFeedback(null);
      }
    };
    const unregister = registerProtectedStateCleaner(
      "admin-scheduling-catalog",
      () => clearState(true),
    );
    load();
    return () => {
      clearState(false);
      unregister();
    };
  }, [load, subjectKey]);

  const mutate = useCallback(
    (request: AdminSchedulingCatalogMutationRequest) => {
      if (
        !canWriteAdminScheduling(subject, capabilityArea) ||
        !mutationGateway ||
        activeMutation.current ||
        loadState.kind !== "ready"
      ) {
        return;
      }
      const idempotencyKey = createSchedulingIdempotencyKey();
      if (!idempotencyKey) {
        setFeedback(
          "The catalog change could not be prepared. Refresh before trying again.",
        );
        return;
      }
      readSequence.current += 1;
      activeRead.current?.abort();
      activeRead.current = null;
      const sequence = mutationSequence.current + 1;
      mutationSequence.current = sequence;
      const controller = new AbortController();
      activeMutation.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();
      setBusy(true);
      setFeedback(null);

      void mutationGateway(request, {
        signal: controller.signal,
        idempotencyKey,
      })
        .then(
          (outcome) => {
            if (
              controller.signal.aborted ||
              mutationSequence.current !== sequence ||
              !isProtectedStateGenerationCurrent(protectedStateGeneration)
            ) {
              return;
            }
            if (
              outcome.kind === "confirmed" &&
              isAdminSchedulingCatalogSnapshot(outcome.catalog)
            ) {
              setLoadState({ kind: "ready", catalog: outcome.catalog });
              setFeedback("The catalog change was confirmed.");
              return;
            }
            setFeedback(
              outcome.kind === "conflict"
                ? "The catalog changed elsewhere. Refresh before editing it."
                : "The catalog change was not confirmed. Refresh before trying again.",
            );
          },
          () => {
            if (
              !controller.signal.aborted &&
              mutationSequence.current === sequence &&
              isProtectedStateGenerationCurrent(protectedStateGeneration)
            ) {
              setFeedback(
                "The catalog change could not be confirmed. Refresh before trying again.",
              );
            }
          },
        )
        .finally(() => {
          if (
            mutationSequence.current === sequence &&
            isProtectedStateGenerationCurrent(protectedStateGeneration)
          ) {
            activeMutation.current = null;
            setBusy(false);
          }
        });
    },
    [capabilityArea, loadState, mutationGateway, subject],
  );

  if (!authorized) {
    return (
      <CatalogFrame title="Resource catalog unavailable">
        <ContentState kind="warning" title="Access denied" live={false}>
          Resource data is only available with the schedule read permission.
        </ContentState>
      </CatalogFrame>
    );
  }

  if (!readGateway) {
    return (
      <CatalogFrame title="Resource catalog unavailable">
        <ContentState
          kind="warning"
          title="Scheduling connection unavailable"
          live={false}
        >
          No production scheduling gateway is configured. No local or synthetic
          data was substituted.
        </ContentState>
      </CatalogFrame>
    );
  }

  if (loadState.kind === "loading") {
    return (
      <CatalogFrame title="Loading resource catalog">
        <ContentState kind="loading" title="Loading confirmed catalog data" />
      </CatalogFrame>
    );
  }

  if (loadState.kind === "error") {
    return (
      <CatalogFrame title="Resource catalog unavailable">
        <ContentState
          kind="error"
          title="Confirmed catalog data could not be loaded"
          actions={
            <button type="button" onClick={load}>
              Retry
            </button>
          }
        />
      </CatalogFrame>
    );
  }

  return (
    <AdminSchedulingCatalogView
      focus={focus}
      catalog={loadState.catalog}
      canWrite={
        canWriteAdminScheduling(subject, capabilityArea) &&
        mutationGateway !== null
      }
      busy={busy}
      feedback={feedback}
      onMutationRequest={mutate}
    />
  );
}
